import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { loadRegistrationsForTable, registrationTableQueryKey, type RegistrationTableRow } from '@/api/registration';
import { type ApiResponse } from '@/api/tenantClient';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import { uiStyles } from '@/lib/ui';
import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import Datatable from '../Registration/Datatable';
import BaseLoadingSpinner from '@/base/Loading/Spinner';
import { type MRT_ColumnFiltersState, type MRT_PaginationState, type MRT_SortingState, type MRT_VisibilityState } from 'mantine-react-table';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useLocalStorage } from '@uidotdev/usehooks';
import CreateRegistrationModal from '@/registration/component/Modal/CreateRegistrationModal';
import { useSavedViews } from '@/lib/hook/useSavedViews';
import { type TableViewConfig } from '@/api/common/tableViews';

export interface SerializedRegistrationTableRow extends Omit<RegistrationTableRow, 'createdAt' | 'confirmDelegateTypeAt'> {
  createdAt: Date
  confirmDelegateTypeAt: Date | null
}

interface Params {
  filters: any
  sorting: any
  pagination: any
  search: string
}

const initialVisibilityState: MRT_VisibilityState = {
  idInfo: false,
  'delegateType.name': false,
  'ticket.function': false,
  'ticket.workshop': false,
  'ticket.tour': false,
  abandonedCart: false,
  isGroupRegistration: false,
  registeredBy: false,
  groupCoordinatorName: false,
  groupCoordinatorCompany: false,
  paid: false,
  complete: false,
  createdAt: false,
  confirmDelegateTypeAt: false,
  'attendeeCategory.name': false,
  completedAtDate: false,
  completedAtTime: false,
  createdAtDate: false,
  createdAtTime: false,
  idPhotocopy: false,
  adminsNotes: false
};

const useGetRegistrations = ({ filters, sorting, pagination, search }: Params & { setPagination: React.Dispatch<React.SetStateAction<any>> }) => {
  const { createTenantAdminApiRequest, isLoading: isTenantLoading } = useTenantApi();
  return useQuery<ApiResponse<RegistrationTableRow>, Error>({
    queryKey: [registrationTableQueryKey, pagination, filters, sorting, search],
    queryFn: async (): Promise<ApiResponse<RegistrationTableRow>> => {
      return await loadRegistrationsForTable(createTenantAdminApiRequest)({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        type: 'all',
        filters,
        sorting,
        search
      });
    },
    keepPreviousData: true,
    staleTime: 30_000,
    enabled: !isTenantLoading,
  });
};

const LOCAL_TABLE_STORAGE_KEY = 'registration_table';
const defaultPaginationState: MRT_PaginationState = { pageIndex: 0, pageSize: 10 };

const RegistrationTable = () => {
  const { tenantCode } = useRegisterRoute();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filters, setFilters] = useLocalStorage<MRT_ColumnFiltersState>(`${LOCAL_TABLE_STORAGE_KEY}_filters_${tenantCode}`, []);
  const [sorting, setSorting] = useLocalStorage<MRT_SortingState>(`${LOCAL_TABLE_STORAGE_KEY}_sorting_${tenantCode}`, []);
  const [search, setSearch] = useLocalStorage<string>(`${LOCAL_TABLE_STORAGE_KEY}_search_${tenantCode}`, '');
  const [pagination, setPagination] = useLocalStorage<MRT_PaginationState>(`${LOCAL_TABLE_STORAGE_KEY}_pagination_${tenantCode}`, defaultPaginationState);
  const [columnVisibility, setColumnVisibility] = useLocalStorage<MRT_VisibilityState>(`${LOCAL_TABLE_STORAGE_KEY}_columns_${tenantCode}`, initialVisibilityState);
  const { reportToGlobal } = useApiErrorHandler();
  const { data, isLoading, isError, error, isFetching, refetch } = useGetRegistrations({ filters, sorting, pagination, search, setPagination });

  const handleApplyView = useCallback((config: TableViewConfig) => {
    const nextFilters = (config.columnFilters ?? []).map(filter => {
      const value = filter.value;
      let clonedValue = value;

      if (Array.isArray(value)) {
        clonedValue = value.map(entry => {
          return entry instanceof Date ? new Date(entry) : entry;
        });
      } else if (value instanceof Date) {
        clonedValue = new Date(value);
      }

      return {
        ...filter,
        value: clonedValue
      };
    });

    setFilters(nextFilters);
    setSorting(config.sorting ? [...config.sorting] : []);
    setColumnVisibility(config.columnVisibility ? { ...config.columnVisibility } : { ...initialVisibilityState });
    setSearch(config.globalFilter ?? '');
    setPagination(config.pagination ? { ...config.pagination } : { ...defaultPaginationState });
  }, [setFilters, setSorting, setColumnVisibility, setSearch, setPagination]);

  const defaultViewConfig = useMemo<TableViewConfig>(() => {
    return {
      columnFilters: [],
      sorting: [],
      columnVisibility: { ...initialVisibilityState },
      columnOrder: [],
      globalFilter: '',
      pagination: { ...defaultPaginationState },
    };
  }, []);

  const savedViews = useSavedViews({
    tableIdentifier: 'registrations',
    currentConfig: {
      columnFilters: filters,
      sorting,
      columnVisibility,
      globalFilter: search,
      pagination,
    },
    onApplyView: handleApplyView,
    defaultConfig: defaultViewConfig
  });

  const handleCreateRegistration = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(
    () => {
      if (error) {
        reportToGlobal(error);
      }
    },
    [error]
  );
  if (!data || isLoading) {
    return (
      <BaseLoadingSpinner />
    );
  }

  if (data.items && Array.isArray(data.items)) {
    return (
      <>
        <Datatable
          data={data.items.map((registration) => {
            return {
              ...registration,
              paid: registration.paid ? 'Yes' : 'No',
              complete: registration.complete ? 'Yes' : 'No',
              createdAt: new Date(registration.createdAt),
              confirmDelegateTypeAt: registration.confirmDelegateTypeAt === null ? null : new Date(registration.confirmDelegateTypeAt)
            };
          })}
          isLoading={isLoading}
          isError={isError}
          isFetching={isFetching}
          refetch={refetch}
          filters={filters}
          setFilters={setFilters}
          sorting={sorting}
          setSorting={setSorting}
          search={search}
          setSearch={setSearch}
          pagination={pagination}
          setPagination={setPagination}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
          rowCount={data.pagination!.total}
          delegateTypeSelectOptions={data.selectOptions!.delegateTypeSelectOptions}
          booleanSelectOptions={data.selectOptions!.booleanSelectOptions}
          statusSelectOptions={data.selectOptions!.statusSelectOptions}
          tourTicketsSelectOptions={data.selectOptions!.tourTicketsSelectOptions}
          workshopTicketsSelectOptions={data.selectOptions!.workshopTicketsSelectOptions}
          functionTicketsSelectOptions={data.selectOptions!.functionTicketsSelectOptions}
          attendeeCategorySelectOptions={data.selectOptions!.attendeeCategorySelectOptions}
          paymentSelectOptions={data.selectOptions!.paymentOptions}
          onCreateRegistration={handleCreateRegistration}
          savedViews={savedViews}
        />
        <CreateRegistrationModal
          isOpen={isCreateModalOpen}
          onClose={() => { return setIsCreateModalOpen(false); }}
          onSuccess={handleCreateSuccess}
        />
      </>
    );
  } else {
    return (
      <BaseMessageBarInfo borderRadius={uiStyles.borderRadius}>
        No registrations
      </BaseMessageBarInfo>
    );
  }
};

export default RegistrationTable;
